import Vue from 'vue';
import store from 'store';
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import { hasTag, getAllValueForTag, decode } from 'utils/general';
import accessibility from 'mixins/accessibility';
import cartData from 'mixins/cart-data';
import { i18n } from 'lib/i18n.js';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import select from 'dom-select';
import { buildItemTrackingData } from 'lib/tracking';
import datalayer from 'lib/datalayer';
import { trackEvent } from '../../assets/js/lib/tracking';
import { A11y, Keyboard, Navigation } from 'swiper/modules';

export default (el) =>
  new Vue({
    components: {
      modal: () => import('../modal/modal.vue'),
      'product-card-slider-swiper': () =>
        import('../product-card-slider-swiper/product-card-slider-swiper.vue'),
      'cart-item': () => import('../cart-item/cart-item.vue'),
      'product-back-in-stock': () =>
        import('../product-back-in-stock/product-back-in-stock.vue'),
      'gift-with-purchase': () =>
        import('../gift-with-purchase/gift-with-purchase.vue'),
      'quick-view': () => import('../quick-view/quick-view.vue'),
      gwp: () => import('../gwp/gwp.vue'),
      'gwp-collection': () => import('../gwp-collection/gwp-collection.vue'),
      geolocation: () => import('../geolocation/geolocation.vue'),
      'vue-image': () => import('../vue-image/vue-image.vue'),
      'vue-image-v2': () => import('../vue-image-v2/vue-image-v2.vue'),
      'vue-swiper': () => import('../vue-swiper/vue-swiper.vue'),
    },
    el,
    store,
    name: 'Header Cart',
    mixins: [accessibility, cartData],
    data() {
      return {
        isActiveCountry: false,
        existingSession:
          window.sessionStorage.getItem('existingSession') === 'true',
        OGOptIn: false,
        samplesThreshold: 0,
        samplesQuantity: 1,
        activeSamples: [],
        samplesCount: 0,
        recommendedSwiperOptions: {
          slidesPerView: 'auto',
          keyboard: true,
          a11y: true,
          modules: [
            Keyboard, A11y, Navigation
          ],
          navigation: {
            nextEl: `#${el.id} .slider-next`,
            prevEl: `#${el.id} .slider-prev`
          },
        },
        swiperOptions: {
          slidesPerView: 1.3,
          spaceBetween: 5,
          keyboard: true,
          a11y: true,
          modules: [
            Keyboard, A11y
          ]
        },
        transitionToCheckout: false,
      };
    },
    watch: {
      open(value) {
        if (value && this.isGWPActive) {
          if (!this.GWPRewardProducts) {
            this.requestRewardsCollection(this.GWPHandle);
            this.setGWPProductDelimiter(__GLOBAL__.productDelimiter);
          }
        }
        if (value && !this.asyncDetailsFetched) {
          this.requestAsyncDetails();
        }

        setTimeout(() => {
          this.checkSubscription();
        }, 500);
      },
    },
    async created() {
      await this.requestAsyncToken();
    },
    mounted() {
      this.setActiveCountryCode(window.Shopify.country);
      this.includeCountry();
      this.trackCartItemLeft();
      this.openCartFromRedirect();
      this.setSamplesData();
      this.handleAccountTracking();
    },
    computed: {
      ...mapState('quickView', ['modalTitle']),
      ...mapState('global', ['activeCountryCode']),
      ...mapGetters('global', [
        'isCartEmpty',
        'isGWPActive',
        'GWPHandle',
        'GWPRewardProducts',
        'totalCartAmount',
      ]),
      cartActive() {
        const cartElement = this.$el.querySelector('.js-header-cart');
        if (this.cartStatus) {
          const firstFocusable = cartElement.querySelector('button, [tabindex="0"], a, input');
          if (firstFocusable) {
            setTimeout(() => {
              firstFocusable.focus();
            }, 500);
          }
          let touchEl = null;
          if (document.querySelector('.header-cart__recommended-product')) {
            touchEl = document.querySelector(
              '.header-cart__recommended-product',
            );
          }
          disableBodyScroll(this.$refs.wrapper, {
            allowTouchMove: (el) => touchEl,
          });
          document.body.classList.add('scroll-lock');
        } else {
          clearAllBodyScrollLocks();
          document.body.classList.remove('scroll-lock');
        }
        return this.cartStatus;
      },
      showSamples() {
        return (
          this.totalCartAmount >= this.samplesThreshold && this.samplesCount > 0
        );
      },
    },
    methods: {
      ...mapActions('cart', [
        'toggleCart',
        'closeCart',
        'openCart',
        'addToCart',
        'updateCartLine',
      ]),
      ...mapMutations('global', [
        'setGWPProductDelimiter',
        'setActiveCountryCode',
      ]),
      handleAccountTracking() {
        const accountBtns = this.$el.querySelectorAll(
          '.js-header-cart-message-new a',
        );
        if (accountBtns) {
          accountBtns.forEach((btn) => {
            btn.addEventListener('click', (e) => {
              if (e.target.title == 'login') {
                trackEvent({
                  event: 'login_start',
                  component_name: 'mini-cart',
                  module_name: 'unlock free shipping',
                  shopify_module_name: 'header cart'
                });
              } else if (e.target.title == 'register') {
                trackEvent({
                  event: 'sign_up_start',
                  component_name: 'mini-cart',
                  module_name: 'unlock free shipping',
                  shopify_module_name: 'header cart'
                });
              }
            });
          });
        }
      },
      openCartFromRedirect() {
        const isCartRedirect = new URLSearchParams(location.search).get(
          'cart_redirect',
        );
        if (!isCartRedirect) return;

        this.openCart();

        // Remove query param
        const url = new URL(location.href);
        url.searchParams.delete('cart_redirect');
        history.replaceState(null, null, url);
      },
      getProductTitle(myTitle) {
        const title = new DOMParser().parseFromString(
          (myTitle.split(window.__GLOBAL__.productDelimiter)[0] || '').trim(),
          'text/html',
        ).documentElement.textContent;
        return title.trim();
      },
      formatEecProductData(lineItemObj, cartCurrency) {
        const isBundled = !!lineItemObj.properties._bundle_uid;
        const hasDiscount =
          lineItemObj.final_price < lineItemObj.compare_at_price;
        const productContext = getAllValueForTag('badge', lineItemObj.tags);
        const payload = {
          item_name: this.getProductTitle(lineItemObj.product_title),
          item_category: lineItemObj.product_type,
          bundled: isBundled,
          avg_stars: lineItemObj.properties?._product_rating || undefined,
          item_id: `${lineItemObj.sku}`,
          product_stock: 'in stock',
          ...(lineItemObj.tags &&
            productContext && {
              product_context: productContext.join('|'),
            }),
          affiliation: lineItemObj.vendor,
          item_brand: lineItemObj.vendor,
          currency: cartCurrency,
          price: lineItemObj.compare_at_price ? lineItemObj.compare_at_price / 100 : lineItemObj.final_price / 100.0,
          item_variant: `${lineItemObj.variant_id}`,
          quantity: lineItemObj.quantity,
          subscription:
            lineItemObj.properties?._subscription_frequency || false,
          item_list_id: lineItemObj.properties?._item_list_id,
          item_list_name: lineItemObj.properties?._item_list_name,
          index: parseInt(lineItemObj.properties?._index),
          origin: lineItemObj.properties?._origin,
          origin_product: lineItemObj.properties?._origin_product,
        };

        if (isBundled) {
          payload.bundle_id = String(lineItemObj.properties._bundle);
          payload.bundle_name = lineItemObj.properties._bundle_title;
        }

        if (hasDiscount) {
          payload.original_price = lineItemObj.compare_at_price / 100.0;
          payload.discount = (lineItemObj.compare_at_price - lineItemObj.final_price) / 100.0;
        }
        return payload;
      },
      toggle() {
        this.toggleCart();
      },
      close() {
        this.closeCart();
        const buttonFocused = document.getElementById(window.__GLOBAL__.cartElTrigger);
        if (buttonFocused) {
          buttonFocused.focus();
          buttonFocused.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        window.__GLOBAL__.cartElTrigger = null; // reset
      },
      includeCountry() {
        if (
          __GLOBAL__.gwpCountries?.includes(this.activeCountryCode) ||
          __GLOBAL__.gwpCountries?.toLowerCase().includes('world')
        ) {
          this.isActiveCountry = true;
        }
        return __GLOBAL__.gwpCountries?.includes(this.activeCountryCode);
      },
      checkSubscription() {
        const hasSubscription = this.cart.items?.some((item) => {
          return item.properties._subscription;
        });
        this.OGOptIn = hasSubscription;
      },
      maxQuantity(item) {
        const maxQuantityProp =
          item.properties &&
          Object.keys(item.properties).find(
            (key) => key === `_${Shopify.country.toUpperCase()}`,
          );
        return item.properties?.[maxQuantityProp] || 5;
      },
      goToCheckout() {
        let validated = true;
        let dataId = '';
        this.transitionToCheckout = true;

        if (this.isCartEmpty) {
          location.href = `${Shopify.routes.root}cart`;
          return;
        }

        if (this.cartItems.length) {
          const gwpItems = this.cartItems.filter(
            (item) => item.properties._gwp,
          );
          if (gwpItems.length >= this.cartItems.length) {
            validated = false;
          }
          this.cartItems.map((item, index) => {
            if (validated && item.quantity > this.maxQuantity(item)) {
              validated = false;
              dataId = item.id;
            }
          });
        }

        if (validated) {
          const cartPrice = this.totalCartAmount / 100.0;
          const cartCurrency = window.Shopify.currency.active;

          let eecProductItems = [];
          let bundleTitles = [];
          let bundleIds = [];

          this.cartItems.forEach((item) => {
            const eecItem = this.formatEecProductData(item, cartCurrency);
            eecProductItems.push(eecItem);
            if (item.properties._bundle_title) {
              bundleTitles.push(item.properties._bundle_title);
            }
            if (item.properties._bundle) {
              bundleIds.push(item.properties._bundle);
            }
          });

          window.localStorage.setItem(
            'pixelData',
            JSON.stringify({
              products: eecProductItems,
              bundleTitles: [...new Set(bundleTitles)].join(', '),
              bundleIds: [...new Set(bundleIds)].join(', '),
              clientStatus: __GLOBAL__.clientStatus || 'Not Client',
              country: Shopify.country,
              loginStatus: __GLOBAL__.loginStatus || 'Not Logged',
              language: __GLOBAL__.language || 'EN',
              hashedEmail: __GLOBAL__.hashedEmail || '',
              userId: __GLOBAL__.userId || ''
            }),
          );
          const preferredLanguage = window.navigator.language.split('-')[0];
          location.href = `${Shopify.routes.root}checkout?locale=${preferredLanguage}`;
        } else {
          const errItem = select(`[data-id="_${dataId}"]`);
          const headerCartWrapper = select('.header-cart__wrapper');
          headerCartWrapper.scrollTop = errItem.offsetTop - 15;
        }
      },
      trackCartItemLeft() {
        if (this.existingSession) return;

        if (!this.existingSession && !this.isCartEmpty) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'cartItemLeft',
          });
        }
        window.sessionStorage.setItem('existingSession', 'true');
      },
      setSamplesData() {
        this.samplesThreshold = this.$el.dataset.samplesThreshold;
        this.samplesQuantity = this.$el.dataset.samplesQuantity;
        this.samplesCount = this.$el.dataset.samplesCount;
        this.activeSamples = this.cartItems
          .map((item) => {
            if (item.properties._sample) {
              return item.variant_id;
            }
          })
          .filter((p) => p);
      },
      updateSamples(id) {
        const indexToRemove = this.activeSamples.findIndex((el) => el === id);

        if (indexToRemove > -1) {
          this.updateCartLine({
            quantity: 0,
            id,
          });
          this.activeSamples.splice(indexToRemove, 1);
        } else {
          if (this.activeSamples.length < this.samplesQuantity) {
            this.activeSamples.push(id);
            this.addToCart({
              id,
              quantity: 1,
              properties: {
                _sample: true,
              },
            });
          }
        }

        this.activeSamples = [...new Set([...this.activeSamples])];
        return this.activeSamples;
      },
    },
    i18n,
  });
